/**
 * @module AppInteraction
 * Handles bi-directional interaction between the NaaP App and the NaaP Kiosk.
 * This module provides the {@link AppInteraction} class which can be used to listen
 * for specific events from the NaaP Kiosk such as receiving context tokens or
 * application configuration.
 *
 * Every message exchanged across the kiosk ↔ iframe boundary already carries a
 * {@link TraceContext}, so trace propagation is not an opt-in concern layered on
 * top of this class - it is intrinsic to it. The `config` event always carries the
 * trace context of the message that triggered it, and `requestContextToken`/
 * `requestConfig` can optionally resolve with the trace context tied to that exact
 * request via `{ withTraceContext: true }`.
 *
 * @example
 * const appInteraction = await AppInteraction.init();
 * appInteraction.on('config', (config, traceContext) => initializeApp(config));
 *
 * @remarks
 * Must be paired with {@link KioskInteraction} in the NaaP Kiosk.
 */

import { EventEmitter } from 'eventemitter3';
import z from 'zod';
import { handleAck, waitForAck } from './ack.js';
import type { AppToKioskMessage, KioskToAppMessage, TraceContext } from './types.js';

type AppEvents = {
  /**
   * Emitted when receiving application configuration from NaaP Kiosk.
   * @param config Application configuration string.
   * @param traceContext Trace context carried by this specific config message, if present.
   */
  config: (config: string, traceContext?: TraceContext) => void;
};

/** Resolved value of {@link AppInteraction.requestContextToken} when called with `{ withTraceContext: true }`. */
export type ContextTokenResponse = Readonly<{
  readonly token: string;
  readonly traceContext?: TraceContext;
}>;

/** Resolved value of {@link AppInteraction.requestConfig} when called with `{ withTraceContext: true }`. */
export type ConfigResponse = Readonly<{
  readonly config: string;
  readonly traceContext?: TraceContext;
}>;

/**
 * Error thrown by the `{ withTraceContext: true }` variants of {@link AppInteraction.requestContextToken}
 * and {@link AppInteraction.requestConfig} when the ACK from the NaaP Kiosk carries a business error.
 *
 * The original error is preserved in `ackError`, while `traceContext` carries the
 * trace context that was attached to that same ACK - so callers can correlate a
 * failure with the distributed trace it happened in without losing the original
 * error. The default (no options) call path continues to reject with the raw
 * `ackError` directly, unchanged from prior releases.
 */
export class AckError extends Error {
  public readonly traceContext?: TraceContext;
  public readonly ackError: unknown;

  public constructor(ackError: unknown, traceContext?: TraceContext) {
    super(ackError instanceof Error ? ackError.message : String(ackError));
    this.name = 'AckError';
    this.ackError = ackError;
    this.traceContext = traceContext;
  }
}

/**
 * Extracts the {@link TraceContext} carried by a message, falling back to the
 * legacy top-level `traceparent` field for backward compatibility with older
 * kiosk builds. Returns `undefined` when the message carries no trace
 * information at all.
 */
function extractTraceContext(message: KioskToAppMessage): TraceContext | undefined {
  const traceContext =
    message.traceContext !== undefined
      ? message.traceContext
      : // TODO Only for backward compatibility with legacy messages. Remove this once legacy messages are no longer used.
        { traceparent: message.traceparent };

  return Object.values(traceContext).some((value) => value !== undefined) ? traceContext : undefined;
}

/**
 * Handles bi-directional interaction between the NaaP App and the NaaP Kiosk.
 *
 * @example
 * const appInteraction = await AppInteraction.init();
 * appInteraction.on('config', (config, traceContext) => initializeApp(config));
 *
 * @remarks
 * Must be paired with {@link KioskInteraction} in the NaaP Kiosk.
 */
export class AppInteraction extends EventEmitter<AppEvents> {
  private messagePort?: MessagePort;
  private _traceContext?: TraceContext;

  private static instance: AppInteraction | null = null;

  private constructor() {
    super();
  }

  /**
   * Returns the latest W3C traceparent received from the kiosk.
   * This is updated whenever a message containing a traceparent is received.
   * Use this to continue the distributed trace started by the kiosk/backend.
   * @deprecated Use the {@link traceContext} getter instead for the full structured
   * trace context (including `tracestate`/`baggage`), or prefer the trace context
   * returned inline by {@link requestContextToken} / {@link requestConfig} (called with
   * `{ withTraceContext: true }`) or delivered with the `config` event when you need
   * the context tied to one specific operation rather than this mutable snapshot.
   * @returns The W3C traceparent string, or undefined if no trace context has been received.
   */
  public getTraceContext(): string | undefined {
    return this._traceContext?.traceparent;
  }

  /**
   * Returns the most recently observed trace context, updated whenever a message
   * carrying one is received (the init handshake, a config push, or an ack).
   *
   * Prefer the trace context returned inline by {@link requestContextToken} / {@link requestConfig}
   * (called with `{ withTraceContext: true }`), or delivered with the `config` event,
   * when you need the context tied to one specific operation rather than this
   * mutable snapshot.
   */
  public get traceContext(): TraceContext | undefined {
    return this._traceContext;
  }

  /**
   * Initializes AppInteraction and waits for the MessageChannel to be ready.
   * @returns Promise resolving to a ready AppInteraction instance
   */
  public static async init(): Promise<AppInteraction> {
    // Make sure if the ui requests another app instance we do not create a new one
    // Otherwise we run into issues with the messagePort being reused which is not allowed
    // In case the page reloads the singleton is destroyed and a new one can be created
    if (AppInteraction.instance) return AppInteraction.instance;
    const localInstance = new AppInteraction();
    AppInteraction.instance = localInstance;

    return new Promise<AppInteraction>((resolve) => {
      function handlePort(event: MessageEvent) {
        if (event.data?.type === 'NAAP_INIT_CHANNEL') {
          localInstance.updateTraceContext(event.data);
          if (event.ports?.[0]) {
            localInstance.messagePort = event.ports[0];
            localInstance.messagePort.onmessage = localInstance.handleMessage.bind(localInstance);
            localInstance.messagePort.start();
          } else {
            window.addEventListener('message', localInstance.handleMessage.bind(localInstance), false);
          }
          window.removeEventListener('message', handlePort);
          resolve(localInstance);
        }
      }
      window.addEventListener('message', handlePort, false);
      window.parent.postMessage({ type: 'APP_REQUEST_CHANNEL' }, '*');
    });
  }

  /**
   * Waits for an ack for the given ackId, resolving with its payload and the trace
   * context carried by that specific ack. Rejects with an {@link AckError}
   * (preserving the original error in `ackError`) if the ack carries a business
   * error. Use this after sending a message that expects an ack.
   */
  private async waitForAck<T = unknown>(
    ackId: string,
    typeParser?: (result: unknown) => T,
  ): Promise<{ readonly value: T; readonly traceContext?: TraceContext }> {
    const ack = await waitForAck(ackId);
    const traceContext = extractTraceContext(ack);

    if (ack.error !== undefined) {
      throw new AckError(ack.error, traceContext);
    }

    return {
      value: typeParser ? typeParser(ack.payload) : (ack.payload as T),
      traceContext,
    };
  }

  /**
   * @internal
   * Handles incoming message validation and event routing.
   */
  private handleMessage(event: MessageEvent) {
    const data = event.data as KioskToAppMessage | undefined;
    if (!data || typeof data !== 'object' || !('type' in data)) {
      return;
    }

    this.updateTraceContext(data);

    switch (data.type) {
      case 'NAAP_CONFIG':
        this.emit('config', data.payload, this._traceContext);
        break;
      case 'ACK_RESPONSE': {
        const ackMsg = data as Extract<KioskToAppMessage, { type: 'ACK_RESPONSE' }>;
        if (ackMsg.ackId) {
          handleAck(ackMsg);
        }
        break;
      }
      default:
        break;
    }
  }

  /**
   * Requests a new context token from the kiosk.
   * If the token is still valid it returns the existing one, otherwise a new one is created and returned.
   */
  public requestContextToken(): Promise<string>;
  /**
   * Requests a new context token from the kiosk, together with the trace context
   * carried by the ack that returned it.
   */
  public requestContextToken(options: { withTraceContext: true }): Promise<ContextTokenResponse>;
  public async requestContextToken(options?: { withTraceContext?: boolean }): Promise<string | ContextTokenResponse> {
    const ackId = crypto.randomUUID();
    const wait = this.waitForAck(ackId, (result) => z.string().parse(result));
    const msg: AppToKioskMessage = { type: 'APP_REQUEST_CONTEXT', ackId };
    this.sendMessage(msg);

    if (options?.withTraceContext) {
      const { value, traceContext } = await wait;
      return { token: value, traceContext };
    }

    return AppInteraction.unwrap(wait).then(({ value }) => value);
  }

  /**
   * Requests the current app's config from the kiosk.
   */
  public requestConfig(): Promise<string>;
  /**
   * Requests the current app's config from the kiosk, together with the trace
   * context carried by the ack that returned it.
   */
  public requestConfig(options: { withTraceContext: true }): Promise<ConfigResponse>;
  public async requestConfig(options?: { withTraceContext?: boolean }): Promise<string | ConfigResponse> {
    const ackId = crypto.randomUUID();
    const wait = this.waitForAck(ackId, (result) => z.string().parse(result));
    const msg: AppToKioskMessage = { type: 'APP_REQUEST_CONFIG', ackId };
    this.sendMessage(msg);

    if (options?.withTraceContext) {
      const { value, traceContext } = await wait;
      return { config: value, traceContext };
    }

    return AppInteraction.unwrap(wait).then(({ value }) => value);
  }

  /**
   * Unwraps the rich {@link AckError} raised internally back down to the original,
   * unwrapped `ackError` - preserving the exact rejection behavior of prior
   * releases for callers that did not opt into `{ withTraceContext: true }`.
   */
  private static async unwrap<T>(
    wait: Promise<{ readonly value: T; readonly traceContext?: TraceContext }>,
  ): Promise<{ readonly value: T }> {
    try {
      return await wait;
    } catch (err) {
      throw err instanceof AckError ? err.ackError : err;
    }
  }

  private sendMessage(message: AppToKioskMessage) {
    if (this.messagePort) {
      this.messagePort.postMessage(message);
    } else {
      window.parent.postMessage(message, '*');
    }
  }

  private updateTraceContext(message: KioskToAppMessage): void {
    const ctx = extractTraceContext(message);
    if (ctx === undefined) {
      return;
    }

    this._traceContext = ctx;
  }
}
